<template>

  <v-container fluid class="mt-0 pt-0 page page-route-info">

    <!-- (data loading spinner) -->
    <v-container class="d-flex flex-column align-center justify-center" v-if="isDataLoading">
      <v-progress-circular indeterminate :size="50" style="margin:30px 0" color="primary" />
    </v-container>

    <v-container fluid class="px-0" v-else-if="route">

      <!-- Breadcrumb & Actions -->
      <v-row class="mt-4 mb-5">
        <v-col cols="12" xs="12" sm="12" md="6" lg="6" class="d-flex align-center justify-start page-breadcrumb-new">
          <!-- (breadcrumb) -->
          <template v-if="$root.activeUserHasRole('admin') || $root.activeUserHasRole('dispatcher')">
            <v-icon small class="mr-2">mdi-directions-fork</v-icon>
            <router-link :to="{ name: 'route-list-created' }">Routes</router-link>
          </template>
          <template v-else-if="$root.activeUserHasRole('driver')">
            <v-icon small class="mr-2">mdi-truck-delivery</v-icon>
            <router-link :to="{ name: 'delivery-list-dispatched' }">My Deliveries</router-link>
          </template>
          <div class="separator">/</div>
          <div class="active-item">{{ route.name }}</div>
        </v-col>
        <v-col cols="12" xs="12" sm="12" md="6" lg="6" class="d-flex align-center justify-sm-center justify-md-end">
          <!-- (download manifest) -->
          <route-manifest-download v-if="showDownload" :routeId="routeId" />
        </v-col>
      </v-row>

      <!-- Test Order indicator -->
      <v-row class="mb-4" v-if="isTestRoute">
        <v-col cols="12" class="d-flex align-center justify-center test-route-banner">
          Test Route
        </v-col>
      </v-row>

      <!------------------------------------->
      <!-- Route Name, Status, and Actions -->
      <!------------------------------------->
      <v-container fluid>
        <v-row>
          <v-col cols="12" xs="12" sm="6" md="6" lg="5">
            <div class="page-super-title">Route</div>
            <div class="page-title">{{ route.name }}</div>
            <div class="mt-5">
              <span class="form-field-label">Status:</span>
              <v-chip label outlined :class="`${route.route_status} ml-3`">
                {{ route.route_status }}
              </v-chip>
            </div>
          </v-col>
          <!-- DISPATCHED, DISPATCHED_EDITING -->
          <v-col
              v-if="(isDispatched || isDispatchedEditing) && canEdit"
              cols="12" xs="12" sm="6" md="6" lg="7"
              :class="route.route_status"
              class="mt-4 d-flex flex-column align-center justify-center action-banner-container">
            <!-- Dispatched -->
            <template v-if="isDispatched">
              <div class="title white--text d-flex align-center">
                <span>Need to make changes?</span>
              </div>
              <div class="mt-3" style="text-align:center;">Changes can be made before the delivery has started.</div>
              <div class="mt-3">
                <v-progress-circular indeterminate :size="24" class="mt-2 mb-1" color="primary" v-if="isRouteUpdating" />
                <v-btn text color="primary" @click="openConfirmDialog" v-else>
                  <v-icon left>mdi-pencil</v-icon>
                  Edit Route
                </v-btn>
              </div>
            </template>
            <!-- Dispatched Editing -->
            <template v-else-if="isDispatchedEditing">
              <div class="title white--text d-flex align-center">
                <v-icon color="amber lighten-3" class="pr-3">mdi-clock-edit-outline</v-icon>
                <span>You are editing a dispatched Route.</span>
              </div>
              <div class="mt-2">Delivery is blocked until this Route is again dispatched.</div>
              <div class="mt-3">
                <v-btn text color="primary" @click="editDispatchedRoute">
                  <v-icon left>mdi-pencil</v-icon>
                  Finish Editing
                </v-btn>
              </div>
            </template>
          </v-col>
          <!-- ON_DELIVERY, RETURNING_TO_HQ, COMPLETED -->
          <v-col
              v-else-if="route.route_status === 'ON_DELIVERY' || route.route_status === 'RETURNING_TO_HQ' || route.route_status === 'COMPLETED'"
              cols="12" xs="12" sm="6" md="6" lg="7"
              :class="route.route_status"
              class="mt-4 d-flex flex-column align-center justify-center action-banner-container">
            <div class="title white--text d-flex align-center">
              <span v-if="route.route_status === 'ON_DELIVERY'">Driver is now on delivery.</span>
              <span v-if="route.route_status === 'RETURNING_TO_HQ'">Driver is returning to the lot.</span>
              <span v-if="route.route_status === 'COMPLETED'">Route has completed.</span>
            </div>
            <div class="mt-3 d-flex align-center justify-center" v-if="route.route_status === 'ON_DELIVERY'">
              <div style="font-size:1.1rem;color:#838383;" class="mr-2">started at</div>
              <div style="font-size:1.18rem;font-weight:500;">{{ formatTime(route.on_delivery_at) }}</div>
            </div>
            <div class="mt-3 d-flex align-center justify-center" v-if="route.route_status === 'RETURNING_TO_HQ'">
              <div style="font-size:1.1rem;color:#838383;" class="mr-2">expected return:</div>
              <div style="font-size:1.18rem;font-weight:500;">{{ formatTime(route.estimated_return_time) }}</div>
            </div>
            <div class="mt-3">
              <v-btn text color="primary" @click="goToDeliveryPage">
                <v-icon left>mdi-truck-delivery</v-icon>
                View Delivery
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-container>

      <!-- Tab Navigation -->
      <v-tabs fixed-tabs show-arrows class="page-navigation mt-4" v-model="activeTab">
        <v-tab @click="$root.goTo('route-info-overview')">Details</v-tab>
        <v-tab @click="$root.goTo('route-info-detailed-status')" v-if="showRouteTrackingAndDirections">Tracking History</v-tab>
        <v-tab @click="$root.goTo('route-info-driving-directions')" v-if="showRouteTrackingAndDirections">Map & Directions</v-tab>
      </v-tabs>

      <!-- Page View -->
      <transition name="fade-flash" mode="out-in">
        <router-view />
      </transition>

    </v-container>

    <!------------------------->
    <!-- Edit Confirm Dialog -->
    <!------------------------->
    <v-dialog persistent max-width="450" v-model="isConfirmDialogOpen">
      <v-card max-width="450px">
        <v-card-title class="d-flex align-center justify-center text-h5">
          Enable editing of this Route?
        </v-card-title>
        <v-card-text style="font-size:1.14rem !important;text-align:center;line-height:32px;" class="px-5 mt-5 dialog-text">
          <div>This will prevent delivery from starting.</div>
          <div>You must re-dispatch the route when ready.</div>
        </v-card-text>
        <v-card-actions class="mt-2">
          <v-btn large text color="primary" class="px-5" @click="isConfirmDialogOpen = false">
            Cancel
          </v-btn>
          <v-spacer />
          <v-btn large color="primary" class="px-5" @click="editDispatchedRoute">
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>

</template>

<script>
import RouteManifestDownload from '@/components/routes/RouteManifestDownload'

export default {
  name: 'RouteInfoRootPage',

  components: {
    RouteManifestDownload
  },

  props: {
    routeId: {
      required: true
    }
  },

  data () {
    return {
      activeTab: 0,
      enableDownload: true,
      isDataLoading: false,
      dataLoadingError: null,
      route: null,
      isConfirmDialogOpen: false,
      isRouteUpdating: false,
      routeUpdatingError: null
    }
  },

  computed: {
    canEdit () {
      return (this.$root.activeUserHasRole('admin') || this.$root.activeUserHasRole('dispatcher'))
    },
    isTestRoute () {
      return this.route.is_test
    },
    orders () {
      return this.route.orders || []
    },
    driver () {
      return this.route.driver
    },
    isDispatched () {
      return this.route.route_status === 'DISPATCHED'
    },
    isDispatchedEditing () {
      return this.route.route_status === 'DISPATCHED_EDITING'
    },
    showDownload () {
      return (this.enableDownload && this.route && this.route.route_status === 'COMPLETED')
    },
    showRouteTrackingAndDirections () {
      return (this.route && this.route.route_status !== 'CANCELED')
    }
  },

  async beforeMount () {
    // Set active tab
    this.setActiveTab()

    // Load the route data
    await this.loadItem()
  },

  beforeDestroy () {
    this.route = null
  },

  methods: {
    setActiveTab () {
      switch (this.$route.name) {
        case 'route-info-overview': this.activeTab = 0; break
        case 'route-info-detailed-status': this.activeTab = 1; break
        case 'route-info-driving-directions': this.activeTab = 2; break
        default: this.activeTab = 0
      }
    },

    openConfirmDialog () {
      this.isConfirmDialogOpen = true
    },

    formatTime (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'h:mm a') : ''
    },

    // Go to the active delivery page (delivery stepper)
    goToDeliveryPage () {
      this.$root.goTo('delivery-stepper', {
        routeId: this.routeId
      })
    },

    // Enable dispatched Route for editing and go to the route builder page
    async editDispatchedRoute () {
      if (this.isDispatchedEditing) {
        // Go to route builder page
        this.$root.goTo('route-builder', {
          routeId: this.route.id
        })
      } else if (this.isDispatched) {
        // Enable for editing and go to builder page
        try {
          this.isRouteUpdating = true

          const updatedRoute = await this.$store.dispatch('sh/dataApi', {
            method: 'post',
            uri: `route/${this.route.id}/edit-dispatched`
          })

          // Go to route builder page
          this.$root.goTo('route-builder', {
            routeId: updatedRoute.id
          })
        } catch (err) {
          this.routeUpdatingError = err.message || err.text || err
        } finally {
          this.isRouteUpdating = false
        }
      } else {
        return false
      }
    },

    // Mount order history
    async mountOrderHistory (route = {}) {
      if (route.orders && route.orders.length > 0) {
        for (const order of route.orders) {
          const orderHistory = await this.$store.dispatch('sh/dataApi', {
            method: 'get',
            uri: `order/${encodeURIComponent(order.order_number)}/order-history`,
            opts: {
              is_test: this.isTestRoute
            },
            toModel: false
          })

          // Mount on order
          if (orderHistory) order.order_history = orderHistory.items
        } // end-for
      }
    },

    async loadItem () {
      this.isDataLoading = true

      // Ensure active route is loaded
      try {
        this.route = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: `route/${this.routeId}`,
          opts: {
            params: { with: 'orders,orders.shipper,orders.receiver,orders.order_details,vehicle,vehicle_lot,driver,dispatcher' }
          }
        })

        // Load relevant assocs onto each order
        await this.mountOrderHistory(this.route)
      } catch (err) {
        this.dataLoadingError = err
      } finally {
        this.isDataLoading = false
      }
    }
  }
}
</script>

<style>
  .page-route-info .border-box {
    border: 1px solid grey;
    border-radius: 20px;
  }

  .page-route-info .icon-back-arrow {
    font-size: 19px;
  }

  .page-route-info .test-route-banner {
    text-transform: uppercase !important;
    font-size: 1.2rem;
    color: #ffffff;
    font-weight: 500;
    background-color: #E65000;
    border-radius: 4px;
    height: 46px;
  }

  .page-route-info .action-banner-container {
    font-size: 1.0rem;
    color: #c9c9c9;
    border-radius: 4px;
  }

  .page-route-info .action-banner-container.DISPATCHED, .action-banner-container.ON_DELIVERY,
  .action-banner-container.RETURNING_TO_HQ, .action-banner-container.COMPLETED {
    border: 1px solid #525252;
  }

  .page-route-info .action-banner-container.DISPATCHED_EDITING {
    border: 1px solid #FF9D29;
  }

  .page-route-info .summary-size {
    max-width: 75%;
  }

  .page-route-info .divider {
    border-bottom: 1px solid #ffffff;
    margin-top: 6px !important;
    margin-bottom: 18px !important;
    margin-left: 10px !important;
    margin-right: 10px !important;
  }

  .page-route-info .divider-below-shipment-details {
    border-bottom: 1px solid #ffffff;
    margin-top: -4px !important;
    margin-bottom: 18px !important;
    margin-left: 10px !important;
    margin-right: 10px !important;
  }

  @media screen and (max-width:700px) {
    .page-route-info .summary-size {
      max-width: 95%;
    }
  }
</style>
